import React, { useEffect, useMemo, useState } from "react";
import {
    UserCard,
    UserCardActions,
    UserCardMain,
    UserCardMeta,
    UserCardSub,
    UserCardTitle,
    UserDisabledChip,
    UsersCardsWrap,
} from "./UserManagement.elements";
import { useContributorsActions, useContributorsState, useAlertActions, useLoadingActions } from "@/store";
import { useAuthState } from "@/store/authSlice";
import UserService from "@/Service/UserService";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    Menu,
    MenuItem,
    Select,
    TextField,
} from "@mui/material";
import { theme } from "@/theme/theme";
import DataLoader from "@/components/Loader/DataLoader/DataLoader";
import NoDataState from "@/components/EmptyData/NoDataState";
import { Controller, useForm } from "react-hook-form";
import { ModalBody, ModalHeader, ModalHeaderCloseButton, ModalHeaderTitle, ModalOverlay } from "../Util/Modal.elements";
import { FormContainer, ButtonWrapper, SubmitButton } from "@/components/AuthForm/AuthSteps/Auth.elements";
import { useNavigate } from "react-router-dom";

const UsersList = () => {
    const { contributors, fetch = {} } = useContributorsState();
    const { searchTerm, currentUser } = useAuthState();
    const {
        fetchContributors,
        deleteContributor,
        changeUserInfo,
        selectContributorId,
        setContributorDisabled,
        sendPasswordReset,
    } = useContributorsActions();
    const navigate = useNavigate();

    const [selectedUser, setSelectedUser] = useState(null);
    const [open, setOpen] = useState(false);

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuUser, setMenuUser] = useState(null);

    const [editUser, setEditUser] = useState(null);

    const [disableTarget, setDisableTarget] = useState(null);

    const [tempPasswordTarget, setTempPasswordTarget] = useState(null);
    const [tempPasswordResult, setTempPasswordResult] = useState(null);

    const { showAlert } = useAlertActions();
    const { showLoading, hideLoading } = useLoadingActions();

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        watch,
        reset,
    } = useForm({ mode: "onSubmit", reValidateMode: "onSubmit" });

    useEffect(() => {
        fetchContributors();
    }, [fetchContributors]);

    const { role } = currentUser || {};
    const isAdmin = role === "admin";

    const handleDelete = (user) => {
        setSelectedUser(user);
        setOpen(true);
    };

    const handleConfirmDelete = () => {
        if (selectedUser) deleteContributor(selectedUser);
        setOpen(false);
    };

    const handleCancelDelete = () => {
        setOpen(false);
    };

    const handleExited = () => {
        setSelectedUser(null);
    };

    const filteredContributors = useMemo(() => {
        const term = (searchTerm || "").toLowerCase();
        const map = new Map();

        (contributors || []).forEach((user) => {
            if (!user?.id) return;

            const name = (user.name || "").toLowerCase();
            const email = (user.email || "").toLowerCase();

            if (!term || name.includes(term) || email.includes(term)) {
                if (!map.has(user.id)) map.set(user.id, user);
            }
        });

        return Array.from(map.values());
    }, [contributors, searchTerm]);

    const isLoading = fetch.status === "loading";
    const hasData = filteredContributors.length > 0;

    const availableRoles = isAdmin
        ? ["admin", "manager", "contributor"]
        : [];

    const handleMenuOpen = (e, user) => {
        if (!isAdmin) return;
        e.stopPropagation();
        setMenuAnchorEl(e.currentTarget);
        setMenuUser(user);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuUser(null);
    };

    const copyText = async (value) => {
        const text = String(value || "").trim();
        if (!text) return;

        try {
            if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
                return;
            }

            if (typeof document !== "undefined") {
                const textarea = document.createElement("textarea");
                textarea.value = text;
                textarea.setAttribute("readonly", "");
                textarea.style.position = "fixed";
                textarea.style.opacity = "0";
                textarea.style.pointerEvents = "none";
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand("copy");
                document.body.removeChild(textarea);
            }
        } catch (error) {
            console.error("Failed to copy text:", error);
        }
    };

    const openEditModal = (user) => {
        setEditUser(user);
        reset({
            name: user?.name || "",
            role: user?.role || "",
        });
    };

    const closeEditModal = () => {
        setEditUser(null);
        reset({ name: "", role: "" });
    };

    const onSubmitEdit = async ({ name, role }) => {
        if (!editUser) return;
        const result = await changeUserInfo({ ...editUser, name, role });
        if (result?.meta?.requestStatus === "fulfilled") {
            closeEditModal();
        }
    };

    const originalName = (editUser?.name || "").trim();
    const originalRole = (editUser?.role || "").trim();

    const nameValue = (watch("name") || "").trim();
    const roleValue = (watch("role") || "").trim();

    const hasChanges = nameValue !== originalName || roleValue !== originalRole;

    const canSubmitEdit =
        !!nameValue &&
        !!roleValue &&
        availableRoles.length > 0 &&
        hasChanges;

    const handleOpenContributorUploads = (user) => {
        if (!user?.id) return;
        selectContributorId(user.id);
        navigate("/uploads");
    };

    const openDisableDialog = (user) => {
        setDisableTarget(user);
    };

    const closeDisableDialog = () => {
        setDisableTarget(null);
    };

    const handleConfirmDisableToggle = async () => {
        if (!disableTarget) return;
        const nextDisabled = !disableTarget.disabled;
        await setContributorDisabled(disableTarget, nextDisabled);
        closeDisableDialog();
    };

    const isSelfRow = (user) => !!user?.id && user.id === currentUser?.id;

    const handleConfirmSetTempPassword = async () => {
        const target = tempPasswordTarget;
        if (!target) return;
        setTempPasswordTarget(null);
        try {
            showLoading();
            const res = await UserService.setTemporaryPassword(target.id);
            hideLoading();
            setTempPasswordResult({
                user: target,
                password: res?.data?.tempPassword || "",
                // Backend reports "true"/"false"; anything else (older backend) counts as sent.
                emailSent: String(res?.data?.emailSent ?? "true") !== "false",
            });
        } catch (error) {
            hideLoading();
            const serverMessage = error?.response?.data?.message;
            showAlert("error", serverMessage || "Failed to set temporary password.");
            console.error("Error setting temporary password:", error?.message);
        }
    };

    return (
        <>
            {isLoading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
                    <DataLoader />
                </div>
            ) : !hasData ? (
                <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
                    <NoDataState />
                </div>
            ) : (
                <UsersCardsWrap>
                    {filteredContributors.map((user) => (
                        <UserCard
                            key={user.id}
                            role="button"
                            tabIndex={0}
                            $disabled={!!user.disabled}
                            onClick={() => handleOpenContributorUploads(user)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    handleOpenContributorUploads(user);
                                }
                            }}
                        >
                            <UserCardMain>
                                <UserCardTitle title={user.name}>{user.name}</UserCardTitle>
                                <UserCardSub title={user.email}>{user.email}</UserCardSub>
                                <UserCardMeta>
                                    {String(user.role || "").replace(/^./, (c) => c.toUpperCase())}
                                    {user.disabled && <UserDisabledChip>Disabled</UserDisabledChip>}
                                </UserCardMeta>
                            </UserCardMain>

                            <UserCardActions>
                                {isAdmin && (
                                    <IconButton
                                        aria-label="actions"
                                        onClick={(e) => handleMenuOpen(e, user)}
                                        sx={{
                                            color: "#AEAEB0",
                                            width: 40,
                                            height: 40,
                                            borderRadius: "50%",
                                            "&:hover": {
                                                backgroundColor: "rgba(255, 255, 255, 0.08)",
                                            },
                                        }}
                                    >
                                        <span style={{ fontSize: 22, lineHeight: 1 }}>⋮</span>
                                    </IconButton>
                                )}
                            </UserCardActions>
                        </UserCard>
                    ))}
                </UsersCardsWrap>
            )}

            <Menu
                anchorEl={menuAnchorEl}
                open={!!menuAnchorEl}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        mt: 1,
                        borderRadius: "12px",
                        overflow: "hidden",
                        background: "rgba(41, 56, 77, 0.85)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(128, 149, 183, 0.22)",
                    },
                }}
            >
                <MenuItem
                    disabled={!menuUser?.name}
                    onClick={() => {
                        const name = menuUser?.name;
                        handleMenuClose();
                        copyText(name);
                    }}
                >
                    Copy name
                </MenuItem>

                <MenuItem
                    disabled={!menuUser?.email}
                    onClick={() => {
                        const email = menuUser?.email;
                        handleMenuClose();
                        copyText(email);
                    }}
                >
                    Copy email
                </MenuItem>

                <MenuItem
                    disabled={availableRoles.length === 0}
                    onClick={() => {
                        const u = menuUser;
                        handleMenuClose();
                        if (u) openEditModal(u);
                    }}
                >
                    Change info
                </MenuItem>

                <MenuItem
                    disabled={!menuUser?.email}
                    onClick={() => {
                        const u = menuUser;
                        handleMenuClose();
                        if (u) sendPasswordReset(u);
                    }}
                >
                    Send password reset
                </MenuItem>

                <MenuItem
                    disabled={(!!menuUser && isSelfRow(menuUser))}
                    onClick={() => {
                        const u = menuUser;
                        handleMenuClose();
                        if (u) setTempPasswordTarget(u);
                    }}
                >
                    Set temporary password
                </MenuItem>

                <MenuItem
                    disabled={!!menuUser && isSelfRow(menuUser)}
                    onClick={() => {
                        const u = menuUser;
                        handleMenuClose();
                        if (u) openDisableDialog(u);
                    }}
                >
                    {menuUser?.disabled ? "Enable account" : "Disable account"}
                </MenuItem>

                <MenuItem
                    disabled={!!menuUser && isSelfRow(menuUser)}
                    onClick={() => {
                        const u = menuUser;
                        handleMenuClose();
                        if (u) handleDelete(u);
                    }}
                    sx={{ color: theme.palette.primary.error }}
                >
                    Delete
                </MenuItem>
            </Menu>

            <Dialog
                open={open}
                onClose={handleCancelDelete}
                TransitionProps={{ onExited: handleExited }}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                sx={{
                    "& .MuiBackdrop-root": {
                        backgroundColor: "rgba(0,0,0,0.55)",
                    },
                    "& .MuiPaper-root": {
                        backgroundColor: theme.palette.primary.dark2,
                        borderRadius: "12px",
                        padding: "20px",
                        textAlign: "center",
                        minWidth: "320px",
                        maxWidth: "calc(100vw - 24px)",
                    },
                }}
            >
                <DialogTitle id="alert-dialog-title" sx={{ paddingBottom: "5px", fontWeight: "12px", color: "#fff" }}>
                    {"Are you sure you want to delete"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        id="alert-dialog-description"
                        sx={{ color: "#fff", fontSize: "18px", fontWeight: "bold" }}
                    >
                        {selectedUser?.name}?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", paddingTop: "16px" }}>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        sx={{
                            backgroundColor: theme.palette.primary.error,
                            color: "#fff",
                            fontWeight: "bold",
                            borderRadius: "8px",
                            padding: "10px 20px",
                            textTransform: "none",
                            "&:hover": { backgroundColor: "#f24155" },
                        }}
                    >
                        Delete
                    </Button>
                    <Button
                        onClick={handleCancelDelete}
                        variant="outlined"
                        sx={{
                            borderColor: "#707070",
                            color: "#fff",
                            fontWeight: "bold",
                            borderRadius: "8px",
                            padding: "10px 20px",
                            textTransform: "none",
                            marginLeft: "16px",
                            "&:hover": { borderColor: "#ffffff" },
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!disableTarget}
                onClose={closeDisableDialog}
                aria-labelledby="disable-dialog-title"
                aria-describedby="disable-dialog-description"
                sx={{
                    "& .MuiBackdrop-root": {
                        backgroundColor: "rgba(0,0,0,0.55)",
                    },
                    "& .MuiPaper-root": {
                        backgroundColor: theme.palette.primary.dark2,
                        borderRadius: "12px",
                        padding: "20px",
                        textAlign: "center",
                        minWidth: "320px",
                        maxWidth: "calc(100vw - 24px)",
                    },
                }}
            >
                <DialogTitle id="disable-dialog-title" sx={{ paddingBottom: "5px", fontWeight: "12px", color: "#fff" }}>
                    {disableTarget?.disabled
                        ? "Enable this account?"
                        : "Disable this account?"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        id="disable-dialog-description"
                        sx={{ color: "#fff", fontSize: "16px" }}
                    >
                        {disableTarget?.disabled
                            ? `${disableTarget?.name} will be able to sign in again. Their data is unchanged.`
                            : `${disableTarget?.name} won't be able to sign in. All their uploads and data remain intact and the account can be re-enabled at any time.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", paddingTop: "16px" }}>
                    <Button
                        onClick={handleConfirmDisableToggle}
                        variant="contained"
                        sx={{
                            backgroundColor: disableTarget?.disabled
                                ? theme.palette.primary.main
                                : theme.palette.primary.error,
                            color: "#fff",
                            fontWeight: "bold",
                            borderRadius: "8px",
                            padding: "10px 20px",
                            textTransform: "none",
                        }}
                    >
                        {disableTarget?.disabled ? "Enable" : "Disable"}
                    </Button>
                    <Button
                        onClick={closeDisableDialog}
                        variant="outlined"
                        sx={{
                            borderColor: "#707070",
                            color: "#fff",
                            fontWeight: "bold",
                            borderRadius: "8px",
                            padding: "10px 20px",
                            textTransform: "none",
                            marginLeft: "16px",
                            "&:hover": { borderColor: "#ffffff" },
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!tempPasswordTarget}
                onClose={() => setTempPasswordTarget(null)}
                aria-labelledby="temp-password-dialog-title"
                sx={{
                    "& .MuiBackdrop-root": {
                        backgroundColor: "rgba(0,0,0,0.55)",
                    },
                    "& .MuiPaper-root": {
                        backgroundColor: theme.palette.primary.dark2,
                        borderRadius: "12px",
                        padding: "20px",
                        textAlign: "center",
                        minWidth: "320px",
                        maxWidth: "calc(100vw - 24px)",
                    },
                }}
            >
                <DialogTitle id="temp-password-dialog-title" sx={{ paddingBottom: "5px", color: "#fff" }}>
                    Set a temporary password?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: "#fff", fontSize: "16px" }}>
                        {`${tempPasswordTarget?.name}'s current password will stop working immediately and they will be signed out everywhere. The new temporary password will be shown to you and emailed to ${tempPasswordTarget?.email}.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", paddingTop: "16px" }}>
                    <Button
                        onClick={handleConfirmSetTempPassword}
                        variant="contained"
                        sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: "#fff",
                            fontWeight: "bold",
                            borderRadius: "8px",
                            padding: "10px 20px",
                            textTransform: "none",
                        }}
                    >
                        Set password
                    </Button>
                    <Button
                        onClick={() => setTempPasswordTarget(null)}
                        variant="outlined"
                        sx={{
                            borderColor: "#707070",
                            color: "#fff",
                            fontWeight: "bold",
                            borderRadius: "8px",
                            padding: "10px 20px",
                            textTransform: "none",
                            marginLeft: "16px",
                            "&:hover": { borderColor: "#ffffff" },
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!tempPasswordResult}
                onClose={() => setTempPasswordResult(null)}
                aria-labelledby="temp-password-result-title"
                sx={{
                    "& .MuiBackdrop-root": {
                        backgroundColor: "rgba(0,0,0,0.55)",
                    },
                    "& .MuiPaper-root": {
                        backgroundColor: theme.palette.primary.dark2,
                        borderRadius: "12px",
                        padding: "20px",
                        textAlign: "center",
                        minWidth: "320px",
                        maxWidth: "calc(100vw - 24px)",
                    },
                }}
            >
                <DialogTitle id="temp-password-result-title" sx={{ paddingBottom: "5px", color: "#fff" }}>
                    Temporary password set
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: "#fff", fontSize: "15px", marginBottom: "12px" }}>
                        {`New temporary password for ${tempPasswordResult?.user?.name} (${tempPasswordResult?.user?.email}):`}
                    </DialogContentText>
                    <div
                        style={{
                            fontFamily: "monospace",
                            fontSize: "20px",
                            fontWeight: "bold",
                            color: "#F5C754",
                            background: "#17212E",
                            border: "1px solid rgba(128, 149, 183, 0.4)",
                            borderRadius: "8px",
                            padding: "12px 16px",
                            userSelect: "all",
                            letterSpacing: "1px",
                        }}
                    >
                        {tempPasswordResult?.password}
                    </div>
                    <DialogContentText sx={{ color: "#AEAEB0", fontSize: "13px", marginTop: "12px" }}>
                        {tempPasswordResult?.emailSent
                            ? "It was also emailed to the user. Ask them to change it after signing in. "
                            : "The email to the user could not be sent, so you must share this password with them yourself. "}
                        This dialog is the only place the password is shown — copy it before closing.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", paddingTop: "8px" }}>
                    <Button
                        onClick={() => copyText(tempPasswordResult?.password)}
                        variant="contained"
                        sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: "#fff",
                            fontWeight: "bold",
                            borderRadius: "8px",
                            padding: "10px 20px",
                            textTransform: "none",
                        }}
                    >
                        Copy password
                    </Button>
                    <Button
                        onClick={() => setTempPasswordResult(null)}
                        variant="outlined"
                        sx={{
                            borderColor: "#707070",
                            color: "#fff",
                            fontWeight: "bold",
                            borderRadius: "8px",
                            padding: "10px 20px",
                            textTransform: "none",
                            marginLeft: "16px",
                            "&:hover": { borderColor: "#ffffff" },
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <ModalOverlay open={!!editUser} onClose={closeEditModal} fullWidth>
                <ModalBody>
                    <ModalHeader $marginBottom={28}>
                        <ModalHeaderTitle>CHANGE INFO</ModalHeaderTitle>
                        <ModalHeaderCloseButton type="button" onClick={closeEditModal}>
                            &times;
                        </ModalHeaderCloseButton>
                    </ModalHeader>

                    <FormContainer>
                        <form onSubmit={handleSubmit(onSubmitEdit)}>
                            <TextField
                                fullWidth
                                label="Name"
                                placeholder="Enter the name"
                                {...register("name", { required: "Name is required" })}
                                error={!!errors.name}
                                helperText={errors.name?.message || " "}
                                sx={{ marginBottom: "12px" }}
                            />

                            <FormControl fullWidth error={!!errors.role}>
                                <InputLabel id="change-role-select-label">Role</InputLabel>
                                <Controller
                                    name="role"
                                    control={control}
                                    defaultValue=""
                                    rules={{ required: "Role is required" }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            labelId="change-role-select-label"
                                            label="Role"
                                            disabled={availableRoles.length === 0}
                                        >
                                            {availableRoles.map((roleOption) => (
                                                <MenuItem key={roleOption} value={roleOption}>
                                                    {roleOption}
                                                </MenuItem>
                                            ))}
                                            {availableRoles.length === 0 && (
                                                <MenuItem value="" disabled>
                                                    No roles available
                                                </MenuItem>
                                            )}
                                        </Select>
                                    )}
                                />
                                <FormHelperText>{errors.role?.message || " "}</FormHelperText>
                            </FormControl>

                            <ButtonWrapper>
                                <SubmitButton buttonState="auth" type="submit" disabled={!canSubmitEdit}>
                                    Change
                                </SubmitButton>
                            </ButtonWrapper>
                        </form>
                    </FormContainer>
                </ModalBody>
            </ModalOverlay>
        </>
    );
};

export default UsersList;